import { LinkError } from '@apollo/client/errors'
import { DatePipe, LowerCasePipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import {
  Observable,
  Subject,
  catchError,
  finalize,
  from,
  map,
  of,
  switchMap,
} from 'rxjs'

import { KpiImportKind } from '../../../../_generated/graphql'
import { FileUploaderComponent } from '../../../components/file-uploader/file-uploader.component'
import {
  DialogResult,
  MessageDialogService,
} from '../../../components/message-dialog/message-dialog.service'
import {
  KpiDashboardDefinition,
  KpiDashboardService,
  KpiImportResultView,
  KpiMonthlyDataSummaryView,
} from '../../../services/kpi-dashboard/kpi-dashboard.service'
import { downloadBlob } from '../../../util/blob-download'
import { captureException } from '../../../util/captureException'
import {
  buildKpiTemplateCsvBlob,
  formatFileSize,
  validateKpiImportFile,
} from '../../../util/file/kpi-import'
import { readFileAsBase64 } from '../../../util/file/reader'

type KpiPreviewRow = KpiImportResultView['previewRows'][number]
type KpiImportError = KpiImportResultView['errors'][number]
type SummaryLoadResult =
  | { success: true; summaries: KpiMonthlyDataSummaryView[] }
  | { success: false; error: unknown }

interface KpiPreviewDisplayRow {
  employeeNumber: string
  userName: string
  fileDisplayName: string | null
  roleTitle: string
  values: string[]
}

@UntilDestroy()
@Component({
  selector: 'app-kpi-dashboard-import-tab',
  standalone: true,
  imports: [
    DatePipe,
    LowerCasePipe,
    FileUploaderComponent,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './kpi-dashboard-import-tab.component.html',
  styleUrl: './kpi-dashboard-import-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KpiDashboardImportTabComponent implements OnInit {
  @Input({ required: true }) dashboard!: KpiDashboardDefinition
  @Input({ required: true }) kind!: KpiImportKind

  readonly formatFileSize = formatFileSize

  period = getCurrentPeriod()
  selectedFile: File | null = null
  previewResult: KpiImportResultView | null = null
  previewDisplayRows: KpiPreviewDisplayRow[] = []
  confirmedResult: KpiImportResultView | null = null
  summaries: KpiMonthlyDataSummaryView[] = []
  isLoadingSummaries = false
  summaryLoadFailed = false
  summaryLoadErrorMessage = ''
  operationMessage = ''
  isPreviewing = false
  isImporting = false
  isDeleting = false
  isDownloadingTemplate = false

  private fileBase64: string | null = null
  private readonly reloadSummaries$ = new Subject<void>()

  constructor(
    private kpiDashboardService: KpiDashboardService,
    private messageDialogService: MessageDialogService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  /** 入稿タブの初期表示用に取込済みデータを取得する。 */
  ngOnInit(): void {
    this.reloadSummaries$
      .pipe(
        switchMap(() => this.fetchSummaries()),
        untilDestroyed(this),
      )
      .subscribe((result) => {
        this.isLoadingSummaries = false
        this.summaryLoadFailed = !result.success
        if (result.success) {
          this.summaries = result.summaries
          this.summaryLoadErrorMessage = ''
        } else {
          captureException(result.error)
          this.summaryLoadErrorMessage = this.buildSummaryLoadErrorMessage(
            result.error,
          )
        }
        this.changeDetectorRef.markForCheck()
      })
    this.loadSummaries()
  }

  get isRecord(): boolean {
    return this.kind === KpiImportKind.Record
  }

  get kindLabel(): string {
    return this.isRecord ? '月次実績' : '月次目標'
  }

  get acceptFileExtensions(): string[] {
    if (!this.isRecord || this.dashboard.parserKey === 'standard') {
      return ['csv']
    }
    return ['xlsx']
  }

  get canDownloadTemplate(): boolean {
    return !this.isRecord || this.dashboard.parserKey === 'standard'
  }

  get isBusy(): boolean {
    return (
      this.isPreviewing ||
      this.isImporting ||
      this.isDeleting ||
      this.isDownloadingTemplate
    )
  }

  get canConfirmImport(): boolean {
    return (
      this.isPeriodValid &&
      this.previewResult != null &&
      this.fileBase64 != null &&
      this.confirmedResult == null &&
      !this.isBusy
    )
  }

  get isPeriodValid(): boolean {
    return /^\d{4}-(0[1-9]|1[0-2])$/.test(this.period)
  }

  get previewErrors(): KpiImportError[] {
    return sortErrors(this.previewResult?.errors ?? [])
  }

  get confirmedErrors(): KpiImportError[] {
    return sortErrors(this.confirmedResult?.errors ?? [])
  }

  get periodInputId(): string {
    return `kpi-import-period-${this.kind.toLowerCase()}`
  }

  onPeriodChanged(): void {
    this.resetResults()
    this.preview()
  }

  onFilesSelected(files: File[]): void {
    if (this.isBusy || files.length === 0) {
      return
    }

    const file = files[0]
    const validationError = validateKpiImportFile(
      file,
      this.acceptFileExtensions,
    )
    if (validationError != null) {
      this.selectedFile = null
      this.resetResults()
      this.showError(validationError.message, validationError.title)
      return
    }

    this.selectedFile = file
    this.resetResults()
    this.preview()
  }

  preview(): void {
    if (!this.selectedFile || !this.isPeriodValid || this.isBusy) {
      return
    }

    this.isPreviewing = true
    this.previewResult = null
    this.previewDisplayRows = []
    this.confirmedResult = null
    this.operationMessage = ''

    from(readFileAsBase64(this.selectedFile))
      .pipe(
        switchMap((fileBase64) => {
          this.fileBase64 = fileBase64
          return this.executeImport(fileBase64, true)
        }),
        finalize(() => {
          this.isPreviewing = false
          this.changeDetectorRef.markForCheck()
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (result) => {
          this.previewResult = result
          this.previewDisplayRows = this.buildPreviewDisplayRows(result)
          this.operationMessage = `プレビューを表示しました。取込予定 ${result.importedRows} 行・エラー ${result.errorCount} 件です。`
        },
        error: (error: unknown) => {
          this.fileBase64 = null
          this.handleOperationError(
            error,
            `${this.kindLabel}のプレビューに失敗しました。`,
          )
        },
      })
  }

  confirmImport(): void {
    if (!this.canConfirmImport || !this.fileBase64) {
      return
    }

    this.isImporting = true
    this.operationMessage = ''
    this.executeImport(this.fileBase64, false)
      .pipe(
        finalize(() => {
          this.isImporting = false
          this.changeDetectorRef.markForCheck()
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (result) => {
          this.loadSummaries()
          this.confirmedResult = result
          this.operationMessage = `${result.importedRows}件を取り込みました。`
        },
        error: (error: unknown) => {
          this.handleOperationError(
            error,
            `${this.kindLabel}の取り込みに失敗しました。`,
          )
        },
      })
  }

  downloadTemplate(): void {
    if (!this.canDownloadTemplate || this.isBusy) {
      return
    }

    this.isDownloadingTemplate = true
    this.kpiDashboardService
      .getImportTemplate(this.dashboard.id, this.kind)
      .pipe(
        finalize(() => {
          this.isDownloadingTemplate = false
          this.changeDetectorRef.markForCheck()
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (template) => {
          this.downloadCsv(template)
        },
        error: (error: unknown) => {
          this.handleOperationError(error, 'テンプレートの取得に失敗しました。')
        },
      })
  }

  deleteMonthlyData(): void {
    if (!this.isPeriodValid || this.isBusy) {
      return
    }

    this.messageDialogService
      .showConfirm(
        `${this.period} の${this.kindLabel}データを一括削除します。削除したデータは元に戻せません。`,
        {
          title: '一括削除の確認',
          primaryButtonText: '削除する',
          secondaryButtonText: 'キャンセル',
        },
      )
      .pipe(untilDestroyed(this))
      .subscribe((dialogResult) => {
        if (dialogResult !== DialogResult.PrimaryButtonClicked) {
          return
        }
        this.executeDelete()
      })
  }

  formatErrorRow(error: KpiImportError): string {
    return error.rowNumber == null ? 'ファイル' : `${error.rowNumber}行目`
  }

  private executeImport(
    fileBase64: string,
    dryRun: boolean,
  ): Observable<KpiImportResultView> {
    if (this.isRecord) {
      return this.kpiDashboardService.importMonthlyRecords(
        this.dashboard.id,
        fileBase64,
        this.period,
        dryRun,
      )
    }

    return this.kpiDashboardService.importMonthlyTargets(
      this.dashboard.id,
      fileBase64,
      this.period,
      dryRun,
    )
  }

  /** 取込・削除後も最新の年月別サマリーを表示する。 */
  loadSummaries(): void {
    this.reloadSummaries$.next()
  }

  /** 取得結果とエラーを値として返し、失敗後も再取得できるようにする。 */
  private fetchSummaries(): Observable<SummaryLoadResult> {
    this.isLoadingSummaries = true
    this.changeDetectorRef.markForCheck()
    return this.kpiDashboardService
      .getMonthlyDataSummaries(this.dashboard.id, this.kind)
      .pipe(
        map((summaries): SummaryLoadResult => ({ success: true, summaries })),
        catchError((error: unknown) =>
          of<SummaryLoadResult>({ success: false, error }),
        ),
      )
  }

  /** サーバーのエラーメッセージがあればそれを、無ければ状況に応じた定型文を返す。 */
  private buildSummaryLoadErrorMessage(error: unknown): string {
    // リンク由来（通信断・非 2xx 応答）のエラーは英語の技術的な文言なので定型文にする
    if (error instanceof Error && !LinkError.is(error) && error.message) {
      return error.message
    }
    return this.summaries.length === 0
      ? '取込済みデータを取得できませんでした。'
      : '最新の状態を取得できませんでした。'
  }

  private executeDelete(): void {
    this.isDeleting = true
    this.operationMessage = ''
    this.changeDetectorRef.markForCheck()
    this.kpiDashboardService
      .deleteMonthlyData(this.dashboard.id, this.period, this.kind)
      .pipe(
        finalize(() => {
          this.isDeleting = false
          this.changeDetectorRef.markForCheck()
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (deletedCount) => {
          this.loadSummaries()
          this.operationMessage = `${deletedCount}件を削除しました。`
          this.selectedFile = null
          this.resetResults(false)
        },
        error: (error: unknown) => {
          this.handleOperationError(
            error,
            `${this.kindLabel}データの削除に失敗しました。`,
          )
        },
      })
  }

  private resetResults(clearMessage = true): void {
    this.fileBase64 = null
    this.previewResult = null
    this.previewDisplayRows = []
    this.confirmedResult = null
    if (clearMessage) {
      this.operationMessage = ''
    }
  }

  private downloadCsv(template: string): void {
    const blob = buildKpiTemplateCsvBlob(template)
    const kindName = this.isRecord ? 'records' : 'targets'

    downloadBlob(blob, `kpi_monthly_${kindName}_template.csv`)
  }

  private handleOperationError(error: unknown, fallback: string): void {
    captureException(error)
    // リンク由来（通信断・非 2xx 応答）のエラーは英語の技術的な文言なので定型文にする
    this.showError(
      error instanceof Error && !LinkError.is(error) && error.message
        ? error.message
        : fallback,
      '処理エラー',
    )
  }

  private showError(message: string, title: string): void {
    this.messageDialogService.showError(message, { title })
  }

  private buildPreviewDisplayRows(
    result: KpiImportResultView,
  ): KpiPreviewDisplayRow[] {
    return result.previewRows.map((row) => ({
      employeeNumber: row.employeeNumber,
      userName: row.userName,
      fileDisplayName: getDifferentFileDisplayName(row),
      roleTitle: row.roleTitle || '—',
      values: this.isRecord
        ? buildMetricValues(row, this.dashboard)
        : buildTargetValues(row, this.dashboard),
    }))
  }
}

function getCurrentPeriod(now: Date = new Date()): string {
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  return `${year}-${month}`
}

function sortErrors(errors: KpiImportError[]): KpiImportError[] {
  return [...errors].sort((left, right) => {
    if (left.rowNumber == null && right.rowNumber != null) {
      return -1
    }
    if (left.rowNumber != null && right.rowNumber == null) {
      return 1
    }
    return (left.rowNumber ?? 0) - (right.rowNumber ?? 0)
  })
}

function getDifferentFileDisplayName(row: KpiPreviewRow): string | null {
  const fileDisplayName = row.fileDisplayName.trim()
  return fileDisplayName !== '' && fileDisplayName !== row.userName.trim()
    ? row.fileDisplayName
    : null
}

function buildMetricValues(
  row: KpiPreviewRow,
  dashboard: KpiDashboardDefinition,
): string[] {
  const metricsByItemId = new Map(
    row.metrics.map((metric) => [metric.itemId, metric]),
  )

  return dashboard.items.flatMap((item) => {
    const metric = metricsByItemId.get(item.id)
    return [formatValue(metric?.denominator), formatValue(metric?.numerator)]
  })
}

function buildTargetValues(
  row: KpiPreviewRow,
  dashboard: KpiDashboardDefinition,
): string[] {
  const targetsByItemId = new Map(
    row.targets.map((target) => [target.itemId, target]),
  )

  return dashboard.items.map((item) => {
    const target = targetsByItemId.get(item.id)
    return target == null ? '未設定' : `${target.targetRate}%`
  })
}

function formatValue(value: number | undefined): string {
  return value == null ? '—' : String(value)
}
